import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { 
  Ship, Package, Users, DollarSign, TrendingUp, 
  ArrowRight, Plus, Globe, Sparkles, AlertTriangle, FileDown
} from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const TradeDashboard = () => {
  const { API, authHeader, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [recentShipments, setRecentShipments] = useState([]);
  const [allShipments, setAllShipments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, shipmentsRes, docsRes] = await Promise.all([
          axios.get(`${API}/trade/stats`, { headers: authHeader() }),
          axios.get(`${API}/trade/shipments`, { headers: authHeader() }),
          axios.get(`${API}/trade/documents`, { headers: authHeader() })
        ]);
        setStats(statsRes.data);
        setRecentShipments(shipmentsRes.data.slice(0, 5));
        setAllShipments(shipmentsRes.data);
        // Documents expired or expiring within 30 days
        const soon = docsRes.data
          .filter(d => d.expiry_date)
          .map(d => ({
            ...d,
            daysLeft: Math.ceil((new Date(d.expiry_date) - Date.now()) / 86400000)
          }))
          .filter(d => d.daysLeft <= 30)
          .sort((a, b) => a.daysLeft - b.daysLeft);
        setExpiringDocs(soon);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API]);

  const kpis = [
    { 
      label: "Total Shipments", 
      value: stats?.total_shipments ?? "—",
      icon: Ship,
      sub: `${stats?.active_shipments ?? 0} active`,
      color: "trade-gold"
    },
    { 
      label: "Delivered", 
      value: stats?.delivered_shipments ?? "—",
      icon: Package,
      sub: "Successfully completed",
      color: "trade-green"
    },
    { 
      label: "Suppliers", 
      value: stats?.suppliers_count ?? "—",
      icon: Users,
      sub: "In your network",
      color: "trade-gold"
    },
    { 
      label: "Total Invested (USD)", 
      value: stats ? `$${stats.total_invested_usd.toLocaleString()}` : "—",
      icon: DollarSign,
      sub: `${stats ? `ETB ${(stats.total_revenue_etb).toLocaleString()}` : ""} revenue`,
      color: "trade-green"
    },
  ];

  const quickLinks = [
    { title: "New Shipment", desc: "Track a new import", icon: Ship, to: "/trade/shipments/new", accent: "gold" },
    { title: "AI Assistant", desc: "Ask about customs & duties", icon: Sparkles, to: "/trade/ai", accent: "gold" },
    { title: "Add Supplier", desc: "Register a Chinese supplier", icon: Users, to: "/trade/suppliers/new", accent: "green" },
    { title: "Cost Calculator", desc: "Calculate import costs", icon: TrendingUp, to: "/trade/calculator", accent: "green" },
  ];

  const shipmentProfit = (s) => {
    const usdToEtb = s.usd_to_etb || 128;
    const costUSD =
      (s.product_cost_usd || 0) +
      (s.shipping_cost_usd || 0) +
      (s.customs_duty_usd || 0) +
      (s.djibouti_fees_usd || 0) +
      (s.agent_fees_usd || 0) +
      (s.other_fees_usd || 0) +
      ((s.ethiopia_transport_etb || 0) / usdToEtb);
    const revenue = s.actual_revenue_etb || 0;
    const profit = revenue - costUSD * usdToEtb;
    const margin = revenue > 0 ? (profit / revenue) * 100 : null;
    return { costUSD, revenue, profit, margin };
  };

  const profitShipments = allShipments.filter(s => {
    const hasCost =
      (s.product_cost_usd || 0) + (s.shipping_cost_usd || 0) +
      (s.customs_duty_usd || 0) + (s.djibouti_fees_usd || 0) +
      (s.agent_fees_usd || 0) + (s.other_fees_usd || 0) +
      (s.ethiopia_transport_etb || 0) > 0;
    return hasCost || (s.actual_revenue_etb || 0) > 0;
  });

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        
        {/* Hero */}
        <motion.div {...fadeInUp} className="mb-12">
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Welcome back</p>
          <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4">
            Your Import <span className="trade-gradient-text">Empire</span>
          </h1>
          <p className="text-white/50 text-lg max-w-2xl">
            Manage your China → Djibouti → Ethiopia imports with precision.
          </p>
        </motion.div>

        {/* Globe strip */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-12 trade-card rounded-sm p-6 flex items-center gap-6 overflow-hidden relative"
        >
          <Globe className="w-10 h-10 trade-gold flex-shrink-0" strokeWidth={1} />
          <div className="flex items-center gap-4 text-sm overflow-hidden flex-1">
            <div className="flex gap-8 trade-marquee whitespace-nowrap">
              {["🇨🇳 Guangzhou → 🇩🇯 Djibouti", "🇩🇯 Djibouti Port → 🇪🇹 Addis Ababa", "💱 RMB · USD · ETB", "📦 Full Container · LCL · Air Freight", "🏭 Yiwu Markets", "🚢 Sea Freight 30-45 days", "✈️ Air Freight 7-10 days"].concat(["🇨🇳 Guangzhou → 🇩🇯 Djibouti", "🇩🇯 Djibouti Port → 🇪🇹 Addis Ababa", "💱 RMB · USD · ETB", "📦 Full Container · LCL · Air Freight", "🏭 Yiwu Markets", "🚢 Sea Freight 30-45 days", "✈️ Air Freight 7-10 days"]).map((text, i) => (
                <span key={i} className="text-white/40">{text}</span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {kpis.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <motion.div
                key={kpi.label}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="trade-card p-6 rounded-sm"
              >
                <div className="flex items-start justify-between mb-6">
                  <Icon className={`w-8 h-8 ${kpi.color}`} strokeWidth={1.5} />
                  <div className="w-2 h-2 rounded-full bg-[#10b981] animate-pulse" />
                </div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-2">{kpi.label}</p>
                <p className="text-3xl md:text-4xl font-serif font-bold mb-2">{loading ? "..." : kpi.value}</p>
                <p className="text-xs text-white/50">{kpi.sub}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Profit Highlight */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="trade-card rounded-sm p-8 mb-12 bg-gradient-to-br from-[#10b981]/10 via-transparent to-[#D4AF37]/10 border-[#10b981]/20"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] trade-green mb-2">Net Profit (ETB)</p>
                <p className="text-4xl md:text-5xl font-serif font-bold">
                  {stats.total_profit_etb >= 0 ? "+" : ""}
                  ETB {stats.total_profit_etb.toLocaleString()}
                </p>
              </div>
              <TrendingUp className="w-16 h-16 trade-green opacity-30" strokeWidth={1} />
            </div>
          </motion.div>
        )}

        {/* Document Expiry Alert */}
        {!loading && expiringDocs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="trade-card rounded-sm p-6 mb-12 border-amber-500/30 bg-amber-500/5"
            data-testid="expiry-alert-banner"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-400 flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <h3 className="font-serif font-semibold text-amber-300 mb-2">Document Expiry Alert</h3>
                <div className="space-y-1">
                  {expiringDocs.map(d => (
                    <p key={d.id} className="text-sm text-white/70" data-testid={`expiry-item-${d.id}`}>
                      <span className="font-semibold">{d.description || d.original_name}</span>
                      {" — "}
                      {d.daysLeft < 0 ? (
                        <span className="text-red-400">expired {Math.abs(d.daysLeft)} day{Math.abs(d.daysLeft) === 1 ? "" : "s"} ago</span>
                      ) : (
                        <span className="text-amber-400">expires in {d.daysLeft} day{d.daysLeft === 1 ? "" : "s"}</span>
                      )}
                    </p>
                  ))}
                </div>
                <Link
                  to="/trade/documents"
                  className="inline-flex items-center gap-2 text-xs trade-gold hover:underline mt-3"
                  data-testid="expiry-open-cabinet-link"
                >
                  Open Documents <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Profit by Shipment */}
        {!loading && profitShipments.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="mb-12"
            data-testid="profit-by-shipment"
          >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <h2 className="text-2xl font-serif font-semibold">Profit by Shipment</h2>
              <div className="flex gap-2">
                <a
                  href={`${API}/trade/reports/profit?format=pdf&auth=${token}`}
                  className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                  data-testid="export-pdf-btn"
                >
                  <FileDown size={14} /> Export PDF
                </a>
                <a
                  href={`${API}/trade/reports/profit?format=csv&auth=${token}`}
                  className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                  data-testid="export-csv-btn"
                >
                  <FileDown size={14} /> Export Excel (CSV)
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {profitShipments.map((s, i) => {
                const p = shipmentProfit(s);
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + i * 0.06 }}
                  >
                    <Link
                      to={`/trade/shipments/${s.id}`}
                      className="group block trade-card p-6 rounded-sm h-full"
                      data-testid={`profit-card-${s.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-5">
                        <h3 className="font-serif font-semibold truncate">{s.shipment_name}</h3>
                        <span className={`status-${s.status} px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wide font-semibold whitespace-nowrap`}>
                          {s.status.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/50">Total Cost</span>
                          <span className="font-mono">${p.costUSD.toLocaleString('en-US', {maximumFractionDigits: 0})}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">Revenue</span>
                          <span className="font-mono">ETB {p.revenue.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-white/10">
                          <span className={p.profit >= 0 ? "trade-green font-semibold" : "text-red-400 font-semibold"}>Profit</span>
                          <span className={`font-mono font-bold ${p.profit >= 0 ? "trade-green" : "text-red-400"}`} data-testid={`profit-value-${s.id}`}>
                            {p.profit >= 0 ? "+" : "-"}ETB {Math.abs(p.profit).toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </span>
                        </div>
                        {p.margin !== null && (
                          <p className="text-[11px] text-white/40 text-right">
                            {p.margin.toFixed(1)}% margin
                          </p>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Quick Actions - Bento */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-serif font-semibold mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {quickLinks.map((link, i) => {
              const Icon = link.icon;
              return (
                <motion.div
                  key={link.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + i * 0.08 }}
                >
                  <Link
                    to={link.to}
                    className="group block trade-card p-6 rounded-sm h-full relative overflow-hidden"
                  >
                    <div className={`absolute inset-x-0 top-0 h-1 ${link.accent === "gold" ? "bg-[#D4AF37]" : "bg-[#10b981]"} opacity-50 group-hover:opacity-100 transition-opacity`} />
                    <Icon className={`w-8 h-8 ${link.accent === "gold" ? "trade-gold" : "trade-green"} mb-4`} strokeWidth={1.5} />
                    <h3 className="font-semibold mb-1">{link.title}</h3>
                    <p className="text-xs text-white/50 mb-4">{link.desc}</p>
                    <div className="flex items-center gap-2 text-xs trade-gold group-hover:gap-3 transition-all">
                      <span>Go</span>
                      <ArrowRight size={12} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Recent Shipments */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif font-semibold">Recent Shipments</h2>
            <Link 
              to="/trade/shipments"
              className="text-sm trade-gold hover:underline flex items-center gap-2"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="trade-card rounded-sm overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-white/40">Loading...</div>
            ) : recentShipments.length === 0 ? (
              <div className="p-12 text-center">
                <Ship className="w-12 h-12 trade-gold mx-auto mb-4 opacity-50" strokeWidth={1} />
                <p className="text-white/50 mb-6">No shipments yet. Start tracking your imports.</p>
                <Link to="/trade/shipments/new">
                  <button className="trade-btn-primary px-6 py-3 rounded-sm inline-flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add First Shipment
                  </button>
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {recentShipments.map((s, i) => (
                  <Link
                    key={s.id}
                    to={`/trade/shipments/${s.id}`}
                    className="flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <Ship className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-semibold">{s.shipment_name}</p>
                        <p className="text-xs text-white/50">
                          {s.supplier_name || "No supplier"} · {s.origin} → {s.destination}
                        </p>
                      </div>
                    </div>
                    <span className={`status-${s.status} px-3 py-1 rounded-sm text-xs uppercase tracking-wide font-semibold`}>
                      {s.status.replace(/_/g, " ")}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default TradeDashboard;
